// Raw cabin enum values from the search form (Home.jsx → CabinClassSelector).
// `transformDuffelOfferFromSerpAPI` in server.js bakes the searched cabin into
// `offer.cabin_class` at the TOP level of the offer object, so when the cart
// later spreads `...flight_data` the booking ends up with
// `flight_data.cabin_class = 'economy'` (or 'business', etc.) on every single
// Defera Flights booking — even though no admin ever touched it. Treating
// those enum strings as an "admin override" caused fare brands like
// "Basic Economy" / "Main Cabin" / "Delta Comfort Classic" to be shadowed by
// the generic "Economy" on the trips dashboard (K46N3U is the report that
// surfaced it). Admin-typed overrides are always human-readable labels with
// caps/spaces, so we filter the raw enum out of the override slot and fall
// through to the fare brand instead.
const RAW_SEARCH_CABIN_ENUM = new Set([
  'economy',
  'premium_economy',
  'business',
  'first',
]);

/**
 * Customer-facing fare brand to use when SerpAPI / Google Flights returns a
 * booking option with NO `option_title`. This happens constantly with budget
 * carriers and many international airlines: Google's feed surfaces a single
 * generic option (often rendered as "Main Cabin") with no real fare context,
 * so the customer is blindsided until they reach the airline's own site.
 *
 * For an economy search that bare option is, in practice, the airline's
 * super-low base fare, so we surface it as "Basic Economy" — an honest,
 * informative label instead of a contextless one.
 *
 * IMPORTANT: we only synthesize a brand for ECONOMY searches. For higher
 * cabins we return '' and let the caller fall through to what it actually
 * knows. Guessing a brand for a premium/business search is exactly the
 * mislabel that 2ZZMU6 (a Qatar Airways business fare tagged "Main Cabin")
 * taught us to avoid. The one premium exception needs evidence on the option
 * itself — see `unbrandedFareNameForOption`.
 *
 * @param {string} cabinClass - the searched cabin enum ('economy', 'business', …)
 * @returns {string} 'Basic Economy' for economy/unknown, '' otherwise
 */
export function unbrandedFareNameForCabin(cabinClass) {
  const c = String(cabinClass || '').trim().toLowerCase();
  if (c === '' || c === 'economy') return 'Basic Economy';
  return '';
}

/**
 * Carriers that sell a restricted BASE tier inside a premium cabin, keyed by
 * the ticketing airline Google names in `book_with`, with the exact label
 * Google gives that tier when it does title the option. A carrier belongs
 * here only on evidence; a wrong entry puts a restrictive label on a fare
 * that isn't restrictive.
 *
 * United: united.com sells Business as Base ("Most restrictive":
 * non-refundable, no changes, seats for a fee, no lounge, one checked bag),
 * Standard (two checked bags) and Flexible. For all-United itineraries Google
 * titles them "Base Business" / "Business" / "Business Fully Refundable"
 * (found while investigating TS2MFP). The options we have stored agree on the bags:
 * United "Business" and "Premium Economy" with "2 free checked bags", and
 * "Base Premium Economy" with "1st checked bag free".
 *
 * Deliberately absent: Delta ("Delta One Classic/Extra", "Delta Premium
 * Select Classic/Extra") and American ("Business", "Flagship Business",
 * "Flagship Business Semiflexible"). No base premium tier appears in anything
 * Google has sent us for them, so there is nothing to infer.
 */
const BASE_PREMIUM_TIERS = Object.freeze({
  united: Object.freeze({
    // Free checked bags on the cabin's full (Standard) tier. An untitled
    // option promising fewer is the base tier.
    fullTierFreeCheckedBags: 2,
    labels: Object.freeze({
      business: 'Base Business',
      premium_economy: 'Base Premium Economy',
    }),
  }),
});

const BASE_PREMIUM_LABELS = Object.values(BASE_PREMIUM_TIERS).flatMap((t) => Object.values(t.labels));

/** "United" and "United Airlines" are the same seller; "United, Allegiant"
 *  (two tickets from two sellers) is not. */
function carrierKey(bookWith) {
  return String(bookWith || '')
    .trim()
    .toLowerCase()
    .replace(/\s+(airlines?|air lines)$/, '');
}

/**
 * Free checked bags one option's `baggage_prices` promises per traveler, or
 * null when it says nothing readable about checked bags. These are the only
 * checked-bag shapes in every option we have stored: "2 free checked bags",
 * "1st checked bag free", "1st checked bag up to 23 kg free",
 * "1st checked bag: 80" (paid) and "No checked bags". Carry-on lines are
 * ignored. A checked-bag line in any other shape returns null: a line we
 * can't read is no evidence at all.
 *
 * @param {unknown} baggagePrices - `baggage_prices` from a Google booking option
 * @returns {number|null}
 */
export function freeCheckedBagsFromBaggagePrices(baggagePrices) {
  if (!Array.isArray(baggagePrices)) return null;
  let free = null;
  for (const raw of baggagePrices) {
    const line = String(raw || '').trim().toLowerCase();
    if (!line.includes('checked')) continue;
    let m;
    let n;
    if ((m = line.match(/^(\d+) free checked bags?$/))) n = Number(m[1]);
    else if ((m = line.match(/^(\d+)(?:st|nd|rd|th) checked bag\b.*\bfree$/))) n = Number(m[1]);
    else if ((m = line.match(/^(\d+)(?:st|nd|rd|th) checked bag:/))) n = Number(m[1]) - 1;
    else if (/^no checked bags?$/.test(line)) n = 0;
    else return null;
    free = Math.max(free ?? 0, n);
  }
  return free;
}

/**
 * Fare brand for ONE booking option leg (`together` / `departing` /
 * `returning`) that Google sent with no `option_title`, or '' when we have no
 * evidence of what it is.
 *
 * - Economy (or unknown) search: 'Basic Economy', as `unbrandedFareNameForCabin`.
 * - Premium Economy / Business search: the carrier's base-tier label, and only
 *   when BOTH hold:
 *     1. the seller sells a base tier in that cabin (BASE_PREMIUM_TIERS), and
 *     2. the option's own bags show it: fewer free checked bags than that
 *        cabin's full tier.
 * - Anything else: ''. No evidence, no brand.
 *
 * Both are required because neither is proof alone. TS2MFP was a United
 * Business search (one ANA leg in Economy) that Google returned untitled with
 * "1st checked bag free"; it was United's Base Business, priced exactly as
 * united.com's Base fare, and we stored it as plain "Business". 2ZZMU6 was an
 * untitled Qatar Airways business fare with "2 free checked bags": Qatar has
 * no entry above and the bags match a full tier, so it still gets no label.
 * And bags alone mislead: Google has sent us Turkish "Business Flex", a
 * flexible fare, with "1st checked bag free".
 *
 * @param {object} leg - one booking option leg (option_title, book_with, baggage_prices, …)
 * @param {string} cabinClass - the searched cabin enum ('economy', 'business', …)
 * @returns {string}
 */
export function unbrandedFareNameForOption(leg, cabinClass) {
  const cabin = String(cabinClass || '').trim().toLowerCase();
  if (cabin === '' || cabin === 'economy') return unbrandedFareNameForCabin(cabin);
  const tiers = BASE_PREMIUM_TIERS[carrierKey(leg?.book_with)];
  const label = tiers?.labels[cabin];
  if (!label) return '';
  const freeBags = freeCheckedBagsFromBaggagePrices(leg?.baggage_prices);
  if (freeBags == null || freeBags >= tiers.fullTierFreeCheckedBags) return '';
  return label;
}

/**
 * One Google booking option with every untitled leg branded by
 * `unbrandedFareNameForOption`. Legs Google titled are never touched, and a
 * leg with no evidence stays untitled. `option_title_inferred: true` marks a
 * title as ours, not Google's, so a later price check knows Google will send
 * that option back untitled (server/serp-fare-tier-price.js).
 *
 * @param {object} option - { together?, departing?, returning?, … }
 * @param {string} cabinClass - the searched cabin enum
 * @returns {object} the same option, or a copy with inferred titles
 */
export function withInferredFareNames(option, cabinClass) {
  if (!option || typeof option !== 'object') return option;
  let next = option;
  for (const legKey of ['together', 'departing', 'returning']) {
    const leg = option[legKey];
    if (!leg || typeof leg !== 'object' || leg.option_title) continue;
    const inferred = unbrandedFareNameForOption(leg, cabinClass);
    if (!inferred) continue;
    if (next === option) next = { ...option };
    next[legKey] = { ...leg, option_title: inferred, option_title_inferred: true };
  }
  return next;
}

/**
 * Is this fare brand a premium cabin's restricted base tier ("Base Business",
 * "Base Premium Economy")? Matches the labels above and nothing looser: a bare
 * "base" would catch JetBlue "Main Base" and Lufthansa "Economy Base", whose
 * rules we have no evidence for.
 *
 * @param {string} fareName
 * @returns {boolean}
 */
export function isBasePremiumFareBrand(fareName) {
  const name = String(fareName || '').toLowerCase().replace(/\s+/g, ' ');
  return BASE_PREMIUM_LABELS.some((label) =>
    new RegExp(`\\b${label.toLowerCase()}\\b`).test(name));
}

/**
 * Single source of truth for extracting the marketing fare name from a booking.
 * Uses the EXACT same logic as FlightDetailsModal (getBookingOption/getCabinClass) and EditCart.
 *
 * Data flow:
 * - Google Flights: bookingOption.option_title (from FlightOptions selection) — primary source
 * - Duffel: segment.passengers_cabin_class_marketing_name, cabin_class_marketing_name
 * - Legacy: custom_title, selectedProduct, cabin_class_marketing_name
 * - Admin override: flight_data.cabin_class (set ONLY by AdminEditModal with a
 *   human-readable label like "Basic Economy" or "First")
 *
 * @param {Object} main - First booking in trip (FlightBooking or plain object)
 * @returns {string} Marketing fare name (e.g. "Main Cabin", "Basic Economy", "Comfort+")
 */
export function getMarketingFareName(main) {
  const flightData = main?.flightData || main?.flight_data || {};
  const slices = flightData.slices || [];
  const firstSlice = slices[0];
  const firstSegment = firstSlice?.segments?.[0];

  // 1. Admin override — flight_data.cabin_class is written by AdminEditModal
  //    ("Override the cabin class displayed in Flight Details") with the
  //    human-readable label the admin types ("Basic Economy", "First", etc.).
  //    Raw search-form enum values that leaked in via the synthesized SerpAPI
  //    offer are NOT overrides and must fall through to the real fare brand.
  //    Comparison is CASE-SENSITIVE on purpose: server.js writes the enum
  //    in exact lowercase, while a human admin would type "First" / "Economy"
  //    capitalized — same word, different intent.
  const adminOverrideRaw =
    typeof flightData?.cabin_class === 'string' ? flightData.cabin_class.trim() : '';
  const adminOverride =
    adminOverrideRaw && !RAW_SEARCH_CABIN_ENUM.has(adminOverrideRaw)
      ? adminOverrideRaw
      : '';
  if (adminOverride) return adminOverride;

  // 1.5 Upgrade label — when an admin marks the trip upgraded (AdminEditModal's
  //     "Upgraded" toggle writes is_upgraded + upgraded_to), the cabin label
  //     must match the "Upgraded to X" card the customer sees, not the
  //     originally-booked fare brand (ICY3KV: upgraded to Business but every
  //     cabin label still said "Main Plus" from bookingOption.option_title).
  //     A typed Cabin Class override (step 1) still wins so admins can
  //     fine-tune the label after an upgrade.
  if (flightData?.is_upgraded) {
    const upgradedTo =
      typeof flightData?.upgraded_to === 'string' ? flightData.upgraded_to.trim() : '';
    if (upgradedTo) return upgradedTo;
  }

  // 2. bookingOption / booking_option (user selection in FlightOptions)
  //    Only return a brand here when the offer actually carries one. SerpAPI /
  //    Google Flights direct-with-airline offers (most of Defera's traffic)
  //    frequently arrive without `option_title`; we used to assume those were
  //    "Main Cabin" because that's the UI fallback in FlightOptions.jsx — but
  //    that label was a presentation-layer guess, not a data fact, and it
  //    silently mis-labelled higher-class fares booked through Google's "best
  //    flights" results (2ZZMU6 was a Qatar Airways business booking that ended
  //    up tagged "Main Cabin"). When `option_title` is absent we fall through
  //    to the remaining fields and ultimately let the search-time cabin enum
  //    surface — that's what we actually know.
  const bookingOpt = flightData.bookingOption || flightData.booking_option;
  if (bookingOpt) {
    const optTitle =
      bookingOpt.together?.option_title ||
      bookingOpt.departing?.option_title ||
      bookingOpt.returning?.option_title;
    if (optTitle) return optTitle;
  }

  // 3. Slice-level fare brand (Duffel offers, non-Google-Flights source).
  //    Duffel round-trip bookings that route through FlightOptions populate
  //    `bookingOption` (step 2) but Duffel one-way bookings skip that step
  //    and land here with `slice.fare_brand_name` as the only carrier of
  //    the fare brand ("Basic Economy" / "Economy Fully Refundable" / etc.).
  //    Without this fallback the customer would see a generic "Economy"
  //    on their Trips card instead of the fare brand they actually booked.
  const sliceFareBrand = firstSlice?.fare_brand_name;
  if (sliceFareBrand) return sliceFareBrand;

  // 4. custom_title / customTitle (legacy)
  const customTitle = flightData.custom_title || flightData.customTitle;
  if (customTitle) return customTitle;

  // 5. selectedProduct / selected_product (legacy)
  const selectedProduct = flightData.selectedProduct || flightData.selected_product;
  if (selectedProduct) return selectedProduct;

  // 6. cabin_class_marketing_name / cabinClassMarketingName (Duffel/legacy)
  const fdName =
    flightData.cabin_class_marketing_name || flightData.cabinClassMarketingName;
  if (fdName) return fdName;

  // 7. Segment-level (Duffel API)
  const segmentName =
    firstSegment?.passengers_cabin_class_marketing_name ||
    firstSegment?.cabin_class_marketing_name;
  if (segmentName) return segmentName;

  // 7. seat_class (Upload/manual bookings — flight_data.seat_class)
  const seatClass = flightData?.seat_class;
  if (seatClass) return seatClass;

  // 8. cabin_class (booking column — generic fallback)
  const cabin = main?.cabinClass || main?.cabin_class;
  if (cabin) return cabin;

  // 9. segment.cabin_class (last fallback)
  if (firstSegment?.cabin_class) return firstSegment.cabin_class;

  return '';
}

/**
 * Checkout-time twin of `getMarketingFareName`. Operates on a raw flight offer
 * (the shape `selectedFlight` / `flight_data` has during checkout, with
 * `bookingOption` still attached from FlightOptions) and returns the airline's
 * actual fare brand to durably persist on the booking row's `cabin_class`
 * column. Without this, every checkout call site (Defera Capital, Defera Zero,
 * Reserve, EditCart push-to-trips) was overwriting the column with the
 * lowercase search-time enum (`'economy'`), so anything reading the column
 * directly — DB queries, the Reserve Trips card's `SliceCard`, the All Trips
 * admin modal, etc. — rendered "Economy" instead of the brand the customer
 * actually picked (e.g. "Level", "Main Cabin", "Polaris Business"). I74BF5
 * was the report that surfaced it.
 *
 * Mirrors the priority chain `getMarketingFareName` uses on a *saved* booking,
 * minus the steps that only exist post-write (the admin-typed override slot in
 * `flight_data.cabin_class`, the Upload form's `seat_class` field, and the
 * `cabin_class` column itself — that column is what we're trying to populate
 * here, so reading it would just hand the bug back to ourselves).
 *
 * @param {Object} offer - selectedFlight or flight_data shape
 * @param {string} [fallback] - returned when no fare brand can be derived
 * @returns {string}
 */
export function resolveMarketingFareNameFromOffer(offer, fallback = '') {
  if (!offer || typeof offer !== 'object') return fallback;

  // Only return a brand when the offer actually carries one. We deliberately
  // do NOT default to "Main Cabin" when `bookingOption` exists without an
  // `option_title`: that's a presentation-layer guess, not a data fact, and it
  // silently mis-labelled at least one higher-class booking (2ZZMU6, Qatar
  // Airways business via Google Flights). Falling through lets the caller's
  // `fallback` (the search-time cabin enum) surface — the honest answer when
  // no brand is recorded.
  const bookingOpt = offer.bookingOption || offer.booking_option;
  if (bookingOpt) {
    const optTitle =
      bookingOpt.together?.option_title ||
      bookingOpt.departing?.option_title ||
      bookingOpt.returning?.option_title;
    if (optTitle) return String(optTitle).trim();
  }

  // Slice-level fare brand (Duffel one-way bookings that skip FlightOptions
  // — see the twin comment in `getMarketingFareName`. Without this the
  // booking row's `cabin_class` column would persist the generic search
  // enum instead of the actual fare brand.
  const firstSlice = offer.slices?.[0];
  const sliceFareBrand = firstSlice?.fare_brand_name;
  if (sliceFareBrand) return String(sliceFareBrand).trim();

  const customTitle = offer.custom_title || offer.customTitle;
  if (customTitle) return String(customTitle).trim();

  const selectedProduct = offer.selectedProduct || offer.selected_product;
  if (selectedProduct) return String(selectedProduct).trim();

  const fdName = offer.cabin_class_marketing_name || offer.cabinClassMarketingName;
  if (fdName) return String(fdName).trim();

  const firstSegment = firstSlice?.segments?.[0];
  const segmentName =
    firstSegment?.passengers_cabin_class_marketing_name ||
    firstSegment?.cabin_class_marketing_name;
  if (segmentName) return String(segmentName).trim();

  return fallback;
}
